import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, PlayCircle, MapPin, Layers, Map as MapIcon, BarChart3, Leaf, Clock, Plane, Truck, Ship, Factory, Map as MapI, MousePointerClick, Info, Navigation, Users, Building, Landmark, Briefcase } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageHero } from "@/components/vig/PageHero";
import VaalMap, { MapLegend } from "@/components/vig/VaalMap";
import { Kpi, KpiStrip, Panel, StatusBadge, LinkArrow } from "@/components/vig/Common";
import { OppCard, ThemeCard, THEME_ICONS } from "@/components/vig/Cards";
import { JourneyRail } from "@/components/vig/JourneyRail";

const FACTS = [
  [Clock, "Johannesburg CBD", "~1 hr drive*"], [Plane, "OR Tambo Int. Airport", "~1 hr drive*"], [Truck, "National freight corridors", "N1 / N3 / R59"],
  [Factory, "Proposed SEZ area", "817 ha [S2]"], [MapI, "Wider land programme", "3,080 ha [S3]"],
];

const Sidebar = () => (
  <aside className="space-y-6 border-l border-slate-500/20 bg-[#06142a]/80 p-5 xl:min-h-full" data-testid="discover-sidebar">
    <div><div className="vig-label mb-4">User journey</div><JourneyRail active="discover" /></div>
    <div><div className="vig-label mb-2">Purpose of this page</div><p className="text-[13px] leading-relaxed text-slate-300">A compelling, evidence-led introduction to the Vaal region: location, development vision and key investment themes — inviting you to explore specific initiatives in more detail.</p></div>
    <div>
      <div className="vig-label mb-2">Key interactions</div>
      {[[MapI, "Interactive regional map with key locations and layers"], [MousePointerClick, "Select a theme to update narrative, map and opportunities"], [Info, "Inspect sources and verification status for every claim"], [Navigation, "Continue to opportunity areas or register interest"]].map(([I, t]) => (
        <div key={t} className="flex items-center gap-3 border-b border-slate-500/15 py-2 text-[12.5px] text-slate-200"><I className="h-4 w-4 shrink-0 text-slate-400" />{t}</div>
      ))}
    </div>
    <div>
      <div className="vig-label mb-3">Target users</div>
      <div className="grid grid-cols-4 gap-2 text-center text-[11px] text-slate-300">
        {[[Users, "Investors"], [Briefcase, "Development partners"], [Landmark, "Government stakeholders"], [Building, "Industry players"]].map(([I, t]) => <div key={t}><I className="mx-auto mb-1 h-6 w-6" strokeWidth={1.3} />{t}</div>)}
      </div>
    </div>
  </aside>
);

export default function Discover() {
  const { data, themes, initiatives, setTour } = useApp();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const theme = sp.get("theme") || "industrial";
  const t = themes[theme] || data.themes[0];
  const [fly, setFly] = useState(null);
  const tInits = useMemo(() => data.initiatives.filter((i) => i.theme === t.id), [data, t]);
  const tOpps = useMemo(() => data.opportunities.filter((o) => o.theme === t.id), [data, t]);
  useEffect(() => {
    if (tInits[0]) setFly({ lat: tInits[0].lat, lng: tInits[0].lng, zoom: 10 });
  }, [t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const startTour = async () => {
    const r = await api.get("/tour");
    setTour({ active: true, step: 0, steps: r.data, startedAt: Date.now() });
  };
  const Icon = THEME_ICONS[t.id];

  return (
    <main data-testid="discover-page" className="grid xl:grid-cols-[1fr_330px]">
      <div className="min-w-0">
        <PageHero code="01 | DISCOVER" title="A Region of Possibility." subtitle="Strategically located. Rich in opportunity. Positioned for a smarter tomorrow."
          body="Explore the Vaal region — a connected ecosystem of people, industry, infrastructure and opportunity, from aviation and logistics to advanced manufacturing, agri-processing, renewable energy, tourism and smart urban development."
          minH="min-h-[620px]"
          actions={<>
            <button data-testid="discover-explore-btn" onClick={() => nav(`/explore?theme=${t.id}`)} className="vig-btn-gold flex items-center gap-2 px-5 py-3 text-sm">Explore the Region <ArrowRight className="h-4 w-4" /></button>
            <button data-testid="discover-watch-btn" onClick={startTour} className="vig-btn-ghost flex items-center gap-2 px-5 py-3 text-sm"><PlayCircle className="h-5 w-5" />Watch Overview</button>
          </>}>
          <div className="vig-glass rise d3 ml-auto hidden w-[300px] p-4 2xl:block" data-testid="quick-facts">
            <div className="mb-2 text-sm font-semibold">Quick Facts</div>
            {FACTS.map(([I, l, v]) => <div key={l} className="flex items-center justify-between border-b border-white/10 py-1.5 text-[12px]"><span className="flex items-center gap-2 text-slate-300"><I className="h-3.5 w-3.5" />{l}</span><span className="text-white">{v}</span></div>)}
            <div className="mt-2 text-[10px] text-slate-400">*Approximate drive times, indicative only.</div>
          </div>
        </PageHero>
        <div className="relative z-10 -mt-28 px-5 lg:px-8">
          <KpiStrip testid="discover-kpis">
            <Kpi icon={MapPin} value="Strategic" label="Location" sub="Gauteng, Free State & trade corridors" />
            <Kpi icon={Layers} value="817 ha" label="Proposed SEZ designation" sub="dtic, 2026" badge={<StatusBadge status="publicly_reported" />} />
            <Kpi icon={MapIcon} value="3,080 ha" label="Broader land programme" sub="Sedibeng, 2026" badge={<StatusBadge status="publicly_reported" />} />
            <Kpi icon={BarChart3} value="7 themes" label="Multi-sector opportunity" />
            <Kpi icon={Leaf} value="Sustainable" label="Growth" sub="Clean energy & inclusive development" />
          </KpiStrip>
        </div>

        <section className="mt-8 px-5 lg:px-8">
          <Panel title="Choose an investment theme" testid="theme-selector">
            <div className="flex flex-wrap gap-2">
              {data.themes.map((x) => {
                const I = THEME_ICONS[x.id];
                return (
                  <button key={x.id} data-testid={`theme-btn-${x.id}`} onClick={() => setSp({ theme: x.id })}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[13px] font-semibold transition-colors ${x.id === t.id ? "border-transparent text-[#06142A]" : "border-slate-500/30 text-slate-200 hover:border-slate-300/60"}`}
                    style={x.id === t.id ? { background: x.color } : {}}>
                    <I className="h-4 w-4" />{x.name}
                  </button>
                );
              })}
            </div>
          </Panel>

          <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_1.25fr]">
            <Panel title="Regional narrative" testid="theme-narrative" right={<StatusBadge status="publicly_reported" />}>
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-lg" style={{ background: `${t.color}26`, color: t.color }}><Icon className="h-6 w-6" /></span>
                <h3 data-testid="theme-narrative-title" className="text-xl font-bold">{t.name}</h3>
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-300">{t.narrative}</p>
              <div className="mt-3 rounded-md border border-slate-500/20 bg-white/[0.03] p-3 text-[12.5px] text-slate-300"><b className="text-white">Critical validation:</b> {t.validation}</div>
              <div className="mt-4 vig-label text-[11px]">Initiative cluster ({tInits.length})</div>
              <div className="mt-2 space-y-2">
                {tInits.map((i) => (
                  <button key={i.id} data-testid={`discover-initiative-${i.id}`} onClick={() => setFly({ lat: i.lat, lng: i.lng, zoom: 12 })} className="vig-row flex w-full items-center justify-between rounded-md border border-slate-500/20 px-3 py-2 text-left">
                    <span><span className="block text-[13px] font-semibold">{i.name}</span><span className="text-[11px] text-slate-400">{i.type} · sources {i.sources.join(", ")}</span></span>
                    <StatusBadge status={i.status} />
                  </button>
                ))}
              </div>
              <div className="mt-4"><LinkArrow to={`/explore?theme=${t.id}`} testid="discover-to-explore-link">Explore {t.short} initiatives in the atlas</LinkArrow></div>
            </Panel>
            <Panel title="Interactive map view" testid="discover-map-panel" bodyClass="p-3">
              <div className="relative">
                <VaalMap height={470} theme={t.id} flyTo={fly} layers={["municipalities", "places", "initiatives", "concept", "corridors", ...(t.id === "industrial" ? ["parcels"] : [])]}
                  onSelect={(k, id) => nav(k === "parcel" ? `/explore?initiative=sez&parcel=${id}` : `/explore?initiative=${id}`)} testid="discover-map" />
                <div className="absolute left-2 top-2 z-[500] w-40"><MapLegend /></div>
              </div>
            </Panel>
          </div>

          <div className="mt-8 flex items-end justify-between">
            <h2 className="text-xl font-semibold">Illustrative opportunities — {t.short}</h2>
            <LinkArrow to="/explore" testid="discover-all-areas-link">Explore all areas</LinkArrow>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="discover-opps">
            {tOpps.length === 0 && <p className="text-sm text-slate-400">No illustrative opportunity records for this theme yet — initiatives shown for context only.</p>}
            {tOpps.map((o) => <OppCard key={o.id} opp={o} onClick={() => nav(`/explore?initiative=${o.initiative_id}&opp=${o.id}`)} />)}
          </div>

          <h2 className="mt-10 text-xl font-semibold">Key Development Components</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7">
            {data.themes.map((x) => <ThemeCard key={x.id} theme={x} active={x.id === t.id} testid={`discover-component-${x.id}`} onClick={() => { setSp({ theme: x.id }); window.scrollTo({ top: 600, behavior: "smooth" }); }} />)}
          </div>
          <p className="mt-3 text-[11px] text-slate-500">{initiatives.sez ? "Theme imagery is conceptual and illustrative — not official project renders." : ""}</p>
        </section>
      </div>
      <Sidebar />
    </main>
  );
}
